import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getAllSubscriptionPlans } from "@/services/subscriptionService";
import { sanitizePhoneNumber } from "@/lib/utils";
import { Country, State, City } from "country-state-city";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Building2,
  User,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Copy,
  Check,
  Mail,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { createCompany } from "@/services/superAdminService";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const AddCompany = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [invitationLink, setInvitationLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [plans, setPlans] = useState([]);

  const [formData, setFormData] = useState({
    // Step 1: Company Details
    company_name: "",
    contact_email: "",
    phone: "",
    address: "",
    industry: "",
    plan_id: "",
    billing_cycle: "",
    number_of_employees: "",
    state: "",
    city: "",
    // Step 2: Admin User
    admin_name: "",
    admin_email: "",
    admin_phone: "",
    is_custom_course: null,
    custom_price_per_user: "",
  });

  const steps = [
    { number: 1, title: "Company Details", icon: Building2 },
    { number: 2, title: "Admin User", icon: User },
    { number: 3, title: "Review & Send", icon: CheckCircle },
  ];

  useEffect(() => {
    const fetchSubPlans = async () => {
      try {
        const list = await getAllSubscriptionPlans();
        setPlans(list);
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("Failed to load subscription plans");
      }

    };

    fetchSubPlans();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.company_name.trim()) {
          toast.error("Company name is required");
          return false;
        }
        if (!formData.contact_email.trim()) {
          toast.error("Contact email is required");
          return false;
        }
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.contact_email)) {
          toast.error("Please enter a valid email address");
          return false;
        }

        if (!formData.industry.trim()) {
          toast.error("Industry is required");
          return false;
        }

        if (!formData.plan_id) {
          toast.error("Subscription Plan is required");
          return false;
        }

        if (!formData.state.trim()) {
          toast.error("State is required");
          return false;
        }

        if (!formData.city.trim()) {
          toast.error("City is required");
          return false;
        }

        return true;

      case 2:
        if (!formData.admin_name.trim()) {
          toast.error("Admin name is required");
          return false;
        }
        if (!formData.admin_email.trim()) {
          toast.error("Admin email is required");
          return false;
        }
        const adminEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!adminEmailRegex.test(formData.admin_email)) {
          toast.error("Please enter a valid admin email address");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
    if (formData.is_custom_course === true) {
      if (!formData.custom_price_per_user) {
        toast.error("Custom price per user is required");
        return false;
      }
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        company_name: formData.company_name,
        contact_email: formData.contact_email,
        phone: formData.phone || null,
        address: formData.address || null,
        state: formData.state || null,
        city: formData.city || null,
        industry: formData.industry || null,
        plan_id: formData.plan_id || null,
        billing_cycle: formData.billing_cycle || null,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_phone: formData.admin_phone || null,
        industry: formData.industry || "Other",
        plan_id: formData.plan_id,
        number_of_employees: formData.number_of_employees,
        is_custom_course: formData.is_custom_course,
        custom_price_per_user:
          formData.is_custom_course === true
            ? Number(formData.custom_price_per_user)
            : null,
        // Settings will be created with defaults by backend, admin can update later
      };
      console.log(payload);

      const response = await createCompany(payload);

      setInvitationLink(response.data.invitation_link);
      setEmailSent(response.data.email_sent);

      toast.success("Company created successfully!");

      // Move to final step
      setCurrentStep(4); // Invitation sent step (changed from 5 to 4)
    } catch (error) {
      console.error("Error creating company:", error);
      toast.error(error.response?.data?.error || "Failed to create company");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(invitationLink);
    setLinkCopied(true);
    toast.success("Invitation link copied to clipboard!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const INDIA_STATES = State.getStatesOfCountry("IN");

  const selectedStateIso = formData.state
    ? INDIA_STATES.find((s) => s.name === formData.state)?.isoCode
    : null;

  const cities = selectedStateIso
    ? City.getCitiesOfState("IN", selectedStateIso).map((c) => c.name)
    : [];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground">
              Company Information
            </h3>

            <div className="space-y-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) =>
                    handleInputChange("company_name", e.target.value)
                  }
                  placeholder="e.g., Acme Corporation"
                />
              </div>

              <div>
                <Label>Contact Email *</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) =>
                    handleInputChange("contact_email", e.target.value)
                  }
                  placeholder="e.g., contact@acme.com"
                />
              </div>

              <div className="flex w-full gap-5">
                {/* State */}
                <div className="w-[50%]">
                  <Label>State *</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(value) => {
                      handleInputChange("state", value);
                      handleInputChange("city", ""); // Reset city when state changes
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIA_STATES.map((state) => (
                        <SelectItem key={state.isoCode} value={state.name}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City */}
                <div className="w-[50%]">
                  <Label>City *</Label>
                  <Select
                    value={formData.city}
                    onValueChange={(value) => handleInputChange("city", value)}
                    disabled={!cities.length || !formData.state}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.length > 0 ? (
                        cities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          Select state first
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex w-full gap-5">
                <div className="w-[50%]">
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) =>
                      handleInputChange(
                        "phone",
                        sanitizePhoneNumber(e.target.value),
                      )
                    }
                    placeholder="e.g., +91-9876543210"
                  />
                </div>
                <div className="w-[50%]">
                  <Label>Number of Employees *</Label>
                  <Input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    min={1}
                    max={9999999} // 👈 AUTO LIMIT
                    value={formData.number_of_employees}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^[0-9]+$/.test(value)) {
                        handleInputChange("number_of_employees", value);
                      }
                    }}
                    placeholder="e.g., 50"
                    className="[&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>

              <div className="flex w-full gap-5">
                <div className="w-[50%]">
                  <Label>Select Industry *</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) =>
                      handleInputChange("industry", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        "Maritime Industry",
                        "IT & Software",
                        "Manufacturing",
                        "Retail",
                        "Healthcare",
                        "Education",
                        "Finance",
                        "Construction",
                        "Other",
                      ].map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[50%]">
                  <Label>Select Plan *</Label>
                  <Select
                    value={
                      formData.plan_id
                        ? `${formData.plan_id}-${formData.billing_cycle}`
                        : ""
                    }
                    onValueChange={(value) => {
                      const parts = value.split("-");
                      const billing_cycle = parts.pop(); // last element
                      const plan_id = parts.join("-"); // rejoin the rest

                      setFormData((prev) => ({
                        ...prev,
                        plan_id,
                        billing_cycle,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Plan" />
                    </SelectTrigger>

                    <SelectContent>
                      {plans.length > 0 ? (
                        plans.flatMap((plan) => {
                          const options = [
                            // Monthly option
                            <SelectItem
                              key={`${plan.plan_id}-monthly`}
                              value={`${plan.plan_id}-monthly`}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium text-foreground">
                                  {plan.plan_name} (Monthly)
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  ₹{plan.price_per_user_monthly}/user/month
                                  {plan.is_trial && (
                                    <span className="ml-1 text-blue-600 font-semibold">
                                      • Free Trial
                                    </span>
                                  )}
                                </span>
                              </div>
                            </SelectItem>,
                          ];

                          // Annual only if not trial
                          if (!plan.is_trial) {
                            options.push(
                              <SelectItem
                                key={`${plan.plan_id}-annual`}
                                value={`${plan.plan_id}-annual`}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium text-foreground">
                                    {plan.plan_name} (Annual)
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    ₹{plan.price_per_user_annual}/user/year
                                  </span>
                                </div>
                              </SelectItem>,
                            );
                          }

                          return options;
                        })
                      ) : (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No plans available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {/* Custom Course Radio */}
                <div>
                  <Label>Custom Pricing *</Label>

                  <RadioGroup
                    value={formData.is_custom_course ? "yes" : "no"}
                    onValueChange={(v) =>
                      handleInputChange("is_custom_course", v === "yes")
                    }
                    className="mt-2 flex items-center gap-4"
                  >
                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="yes" id="is_custom_course_yes" />
                      <Label
                        htmlFor="is_custom_course_yes"
                        className="cursor-pointer"
                      >
                        Yes
                      </Label>
                    </div>

                    <div className="flex items-center space-x-1">
                      <RadioGroupItem value="no" id="is_custom_course_no" />
                      <Label
                        htmlFor="is_custom_course_no"
                        className="cursor-pointer"
                      >
                        No
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Custom Price */}
                {formData.is_custom_course ? (
                  <div>
                    <Label>Custom Price Per User *</Label>
                    <Input
                      type="number"
                      min={1}
                      onWheel={(e) => e.target.blur()}
                      value={formData.custom_price_per_user}
                      onChange={(e) =>
                        handleInputChange(
                          "custom_price_per_user",
                          e.target.value,
                        )
                      }
                      placeholder="e.g., 199"
                      className="[&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.billing_cycle && (
                        <>Price per user {formData.billing_cycle}</>
                      )}
                    </p>
                  </div>
                ) : (
                  <div /> // keeps grid alignment when hidden
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground">
              Admin User Details
            </h3>
            <p className="text-sm text-muted-foreground">
              This person will receive an invitation email to set up their admin
              account
            </p>

            <div className="space-y-4">
              <div>
                <Label>Admin Name *</Label>
                <Input
                  value={formData.admin_name}
                  onChange={(e) =>
                    handleInputChange("admin_name", e.target.value)
                  }
                  placeholder="e.g., John Doe"
                />
              </div>

              <div>
                <Label>Admin Email *</Label>
                <Input
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) =>
                    handleInputChange("admin_email", e.target.value)
                  }
                  placeholder="e.g., admin@acme.com"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Invitation will be sent to this email
                </p>
              </div>

              <div>
                <Label>Admin Phone (Optional)</Label>
                <Input
                  value={formData.admin_phone}
                  onChange={(e) =>
                    handleInputChange("admin_phone", sanitizePhoneNumber(e.target.value))
                  }
                  placeholder="e.g., +91-9876543210"

                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground">
              Review & Confirm
            </h3>
            <p className="text-sm text-muted-foreground">
              Please review the information before creating the company
            </p>

            <div className="space-y-4">
              {/* Company Details */}
              <div className="p-4 rounded-lg border border-border">
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Building2 className="h-4 w-4 mr-2" />
                  Company Details
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Name:
                    </span>
                    <span className="font-medium text-foreground">
                      {formData.company_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Email:
                    </span>
                    <span className="font-medium text-foreground">
                      {formData.contact_email}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Phone:
                    </span>
                    <span className="font-medium text-foreground">
                      {formData.phone}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Employees:
                    </span>
                    <span className="font-medium text-foreground">
                      {formData.number_of_employees}
                    </span>
                  </div>
                </div>
              </div>

              {/* Admin Details */}
              <div className="p-4 rounded-lg border border-border">
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  Admin User
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Name:
                    </span>
                    <span className="font-medium text-foreground">
                      {formData.admin_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Email:
                    </span>
                    <span className="font-medium text-foreground">
                      {formData.admin_email}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-900 dark:text-blue-300">
                <Mail className="h-4 w-4 inline mr-2" />
                An invitation email will be sent to{" "}
                <strong>{formData.admin_email}</strong> to set up their admin
                account. The admin can configure company settings after login.
              </p>
            </div>
          </div>
        );

      // Case 4 is handled below - Invitation Sent Success Screen

      case 4:
        // Invitation Sent - Success Screen
        return (
          <div className="text-center space-y-6 py-8">
            <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Company Created Successfully!
              </h3>
              <p className="text-muted-foreground">
                {formData.company_name} has been added to the platform
              </p>
            </div>

            {/* Invitation Link */}
            <div className="p-6 rounded-lg border-2 border-dashed border-border bg-muted">
              <h4 className="font-medium text-foreground mb-3">
                Admin Invitation Link
              </h4>

              {emailSent && (
                <div className="mb-4 p-3 rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-300">
                    ✉️ Invitation email sent to {formData.admin_email}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Input
                  value={invitationLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  className="flex-shrink-0"
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>

              <p className="mt-3 text-xs text-muted-foreground">
                Share this link with the admin to set up their account. Link
                expires in 7 days.
              </p>
            </div>

            <div className="flex gap-4 justify-center pt-4">
              <Button
                onClick={() => navigate("/super-admin/companies")}
                variant="outline"
              >
                View All Companies
              </Button>
              <Button
                onClick={() => {
                  setCurrentStep(1);
                  setFormData({
                    company_name: "",
                    contact_email: "",
                    phone: "",
                    address: "",
                    admin_name: "",
                    admin_email: "",
                    admin_phone: "",
                  });
                  setInvitationLink("");
                  setEmailSent(false);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Company
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/super-admin/companies")}
          disabled={loading}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Add New Company
          </h1>
          <p className="mt-1 text-muted-foreground">
            Onboard a new company to the Zenith platform
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      {currentStep < 4 && (
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.number === currentStep;
            const isCompleted = step.number < currentStep;

            return (
              <React.Fragment key={step.number}>
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive
                        ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white"
                        : isCompleted
                          ? "bg-green-500 text-white"
                          : "bg-muted text-muted-foreground"
                      }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p
                      className={`text-sm font-medium ${isActive
                          ? "text-foreground"
                          : "text-muted-foreground"
                        }`}
                    >
                      {step.title}
                    </p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${isCompleted
                        ? "bg-green-500"
                        : "bg-muted"
                      }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Form Card */}
      <Card>
        <CardContent className="p-8">{renderStepContent()}</CardContent>
      </Card>

      {/* Navigation Buttons */}
      {currentStep < 4 && (
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || loading}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {currentStep < 3 ? (
            <Button onClick={handleNext} disabled={loading}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-primary hover:bg-primary"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Company...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Create Company & Send Invitation
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AddCompany;
